import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Building2, Pencil, X, Check } from 'lucide-react'
import { departmentsApi } from '@/lib/api'

interface Department {
  id: number
  name: string
  code: string | null
  is_active: boolean
  created_at: string
}

export default function DepartmentsPage() {
  const [newName, setNewName] = useState('')
  const [newCode, setNewCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editCode, setEditCode] = useState('')
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.list(),
    staleTime: 30_000,
  })

  const departments: Department[] = data?.data ?? []

  const createMut = useMutation({
    mutationFn: (d: { name: string; code?: string }) => departmentsApi.create(d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      setNewName('')
      setNewCode('')
      setError(null)
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.detail ?? 'Erro ao criar departamento'
      setError(msg === 'Department already exists' ? 'Esse departamento já existe.' : msg)
    },
  })

  const updateMut = useMutation({
    mutationFn: ({ id, name, code }: { id: number; name: string; code: string }) =>
      departmentsApi.update(id, { name, code: code || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      setEditingId(null)
    },
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => departmentsApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['departments'] }),
  })

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    const name = newName.trim()
    if (!name) return
    createMut.mutate({ name, code: newCode.trim() || undefined })
  }

  function startEdit(dept: Department) {
    setEditingId(dept.id)
    setEditName(dept.name)
    setEditCode(dept.code ?? '')
  }

  function saveEdit(id: number) {
    const name = editName.trim()
    if (!name) return
    updateMut.mutate({ id, name, code: editCode.trim() })
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Departamentos</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gerir departamentos organizacionais
          </p>
        </div>
      </div>

      {/* Create form */}
      <div className="mt-6 card p-5">
        <h2 className="mb-3 text-sm font-semibold text-gray-700">Novo departamento</h2>
        <form onSubmit={handleCreate} className="flex gap-3">
          <input
            className="input flex-1"
            placeholder="Nome do departamento (ex: Produção)"
            value={newName}
            onChange={e => { setNewName(e.target.value); setError(null) }}
            maxLength={200}
          />
          <input
            className="input w-32"
            placeholder="Código (opcional)"
            value={newCode}
            onChange={e => setNewCode(e.target.value)}
            maxLength={50}
          />
          <button
            type="submit"
            className="btn btn-primary flex items-center gap-2"
            disabled={createMut.isPending || !newName.trim()}
          >
            <Plus className="h-4 w-4" />
            {createMut.isPending ? 'A criar...' : 'Criar'}
          </button>
        </form>
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </div>

      {/* List */}
      <div className="mt-6 card overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : departments.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <Building2 className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p>Sem departamentos. Cria o primeiro acima.</p>
          </div>
        ) : (
          <table className="table min-w-full">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Código</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {departments.map(dept => (
                <tr key={dept.id}>
                  <td>
                    {editingId === dept.id ? (
                      <input
                        className="input w-full"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        autoFocus
                        maxLength={200}
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-primary-500 shrink-0" />
                        <span className="font-medium text-gray-900">{dept.name}</span>
                      </div>
                    )}
                  </td>
                  <td>
                    {editingId === dept.id ? (
                      <input
                        className="input w-28"
                        value={editCode}
                        onChange={e => setEditCode(e.target.value)}
                        placeholder="Código"
                        maxLength={50}
                      />
                    ) : (
                      <span className="text-sm text-gray-500">{dept.code ?? '—'}</span>
                    )}
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {editingId === dept.id ? (
                        <>
                          <button
                            className="btn btn-ghost p-1 text-green-600 hover:bg-green-50"
                            onClick={() => saveEdit(dept.id)}
                            disabled={updateMut.isPending}
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            className="btn btn-ghost p-1 text-gray-500 hover:bg-gray-100"
                            onClick={() => setEditingId(null)}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn btn-ghost p-1 text-blue-600 hover:bg-blue-50"
                            title="Editar"
                            onClick={() => startEdit(dept)}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            className="btn btn-ghost p-1 text-red-500 hover:bg-red-50"
                            title="Eliminar"
                            onClick={() => {
                              if (confirm(`Eliminar o departamento "${dept.name}"?`)) {
                                deleteMut.mutate(dept.id)
                              }
                            }}
                            disabled={deleteMut.isPending}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
